import { ArrowRight, Check, Crosshair, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { HealthState, RoutedReason } from '../../gql/types';
import {
  SimulateRouteStatus,
  type SimulateRouteCandidate,
  type SimulateRouteResult,
} from '../../gql/operations';
import { providerLabel } from '../shared/providerLabels';

// Renders a simulateRoute result as a chain visualization (W5): the matched policy name + scope,
// every candidate hop marked eligible ✓ / excluded ✗ + reason, and the SELECTED hop strongly
// highlighted with its routedReason label. Empty states for NO_POLICY and NO_ELIGIBLE_PROVIDER.

const HEALTH_DOT: Record<HealthState, string> = {
  [HealthState.Healthy]: 'bg-emerald-400',
  [HealthState.Degraded]: 'bg-amber-400',
  [HealthState.Down]: 'bg-rose-400',
};

const ROUTED_REASON_LABEL: Record<RoutedReason, string> = {
  [RoutedReason.Preferred]: 'preferred',
  [RoutedReason.Overflow]: 'overflow',
  [RoutedReason.UnhealthyFailover]: 'unhealthy failover',
  [RoutedReason.RetryFallback]: 'retry fallback',
  [RoutedReason.Override]: 'override',
};

function hopRole(index: number): string {
  return index === 0 ? 'Primary' : `Fallback ${index}`;
}

function CandidateHop({
  candidate,
  index,
  selected,
  routedReason,
}: {
  candidate: SimulateRouteCandidate;
  index: number;
  selected: boolean;
  routedReason: RoutedReason | null;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { provider, eligible } = candidate;
  const disabled = !provider.enabled;

  const ring = selected
    ? isDark
      ? 'border-indigo-400/60 bg-indigo-500/15 ring-2 ring-indigo-400/40'
      : 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-300'
    : eligible
      ? isDark
        ? 'border-white/[0.08] bg-white/[0.04]'
        : 'border-slate-200 bg-white'
      : isDark
        ? 'border-rose-500/20 bg-rose-500/[0.06]'
        : 'border-rose-200 bg-rose-50/60';

  return (
    <div
      data-testid="sim-hop"
      data-selected={selected || undefined}
      data-eligible={eligible}
      aria-label={`${hopRole(index)}: ${provider.displayName} — ${
        eligible ? 'eligible' : `excluded${candidate.exclusionReason ? `, ${candidate.exclusionReason}` : ''}`
      }${selected ? ', selected' : ''}`}
      className={`flex min-w-[10rem] flex-col gap-1.5 rounded-xl border px-3 py-2.5 ${ring} ${
        disabled ? 'opacity-70' : ''
      }`}
    >
      <span className="flex items-center justify-between gap-2">
        <span
          className={`text-[10px] font-medium uppercase tracking-widest ${
            isDark ? 'text-white/30' : 'text-slate-400'
          }`}
        >
          {hopRole(index)}
        </span>
        {eligible ? (
          <Check
            className={isDark ? 'h-4 w-4 text-emerald-300' : 'h-4 w-4 text-emerald-600'}
            aria-hidden="true"
          />
        ) : (
          <X
            className={isDark ? 'h-4 w-4 text-rose-300' : 'h-4 w-4 text-rose-600'}
            aria-hidden="true"
          />
        )}
      </span>

      <span
        className={`truncate text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-800'}`}
      >
        {provider.displayName}
      </span>

      <span className="flex items-center gap-1.5">
        <span
          className={`h-2 w-2 flex-shrink-0 rounded-full ${HEALTH_DOT[provider.health.state]}`}
          aria-hidden="true"
        />
        <span className={`text-[11px] ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
          {providerLabel(provider.type)}
          {candidate.providerModel ? ` · ${candidate.providerModel}` : ' · no model alias'}
        </span>
      </span>

      {!eligible && candidate.exclusionReason && (
        <span className={`text-[11px] ${isDark ? 'text-rose-300/80' : 'text-rose-600'}`}>
          {candidate.exclusionReason}
        </span>
      )}

      {selected && (
        <span
          className={`mt-0.5 inline-flex w-fit items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            isDark ? 'bg-indigo-500/20 text-indigo-200' : 'bg-indigo-100 text-indigo-700'
          }`}
        >
          <Crosshair className="h-3 w-3" aria-hidden="true" />
          selected{routedReason ? ` · ${ROUTED_REASON_LABEL[routedReason]}` : ''}
        </span>
      )}
    </div>
  );
}

export interface RouteSimulationResultProps {
  result: SimulateRouteResult;
}

export function RouteSimulationResult({ result }: RouteSimulationResultProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (result.status === SimulateRouteStatus.NoPolicy) {
    return (
      <div
        data-testid="sim-no-policy"
        className={`rounded-xl border border-dashed px-4 py-6 text-center text-sm ${
          isDark ? 'border-white/[0.1] text-white/50' : 'border-slate-300 text-slate-500'
        }`}
      >
        No enabled policy matches — create one.
      </div>
    );
  }

  const selectedPosition = result.selected?.position ?? null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-800'}`}>
          {result.policy?.name ?? 'Matched policy'}
        </span>
        <span
          className={`rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
            result.status === SimulateRouteStatus.Ok
              ? isDark
                ? 'bg-emerald-500/15 text-emerald-300'
                : 'bg-emerald-50 text-emerald-700'
              : isDark
                ? 'bg-rose-500/15 text-rose-300'
                : 'bg-rose-50 text-rose-700'
          }`}
        >
          {result.status === SimulateRouteStatus.Ok ? 'routed' : 'no eligible provider'}
        </span>
      </div>

      {result.status === SimulateRouteStatus.NoEligibleProvider && (
        <p
          data-testid="sim-no-eligible"
          className={`text-sm ${isDark ? 'text-rose-300/90' : 'text-rose-600'}`}
        >
          Policy &lsquo;{result.policy?.name ?? 'unknown'}&rsquo; matched but every provider was
          excluded:
        </p>
      )}

      <ol aria-label="Simulated route" className="flex flex-wrap items-stretch gap-2">
        {result.candidates.map((candidate, index) => (
          <li key={`${candidate.provider.id}-${candidate.position}`} className="flex items-center gap-2">
            {index > 0 && (
              <ArrowRight
                className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-white/25' : 'text-slate-300'}`}
                aria-hidden="true"
              />
            )}
            <CandidateHop
              candidate={candidate}
              index={index}
              selected={selectedPosition === candidate.position}
              routedReason={result.routedReason}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}
